import { fetchData } from '../fetchapi/fetchapi';

export async function getPipelineGraph() {
  return fetchData('pipeline_graph');
}

export async function getPipelineOverview() {
  return fetchData('pipeline_overview');
}

// ── Workflows (many node-graphs per project, run independently) ─────────────
export async function listWorkflows() {
  return fetchData('workflows'); // { workflows: [{id,name,position}] }
}
export async function createWorkflow(name) {
  return fetchData('workflows', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
}
export async function renameWorkflow(id, name) {
  return fetchData(`workflows/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
}
export async function deleteWorkflow(id) {
  return fetchData(`workflows/${id}`, { method: 'DELETE' });
}

// ── Node model (unified authoring surface) ──────────────────────────────────
// Everything is a node (load/transform/save) connected by links. The catalog is
// the library; nodes+links are the graph.
export async function getNodeCatalog() {
  return fetchData('node_catalog'); // { connectors, transforms, strategies }
}
// Project views = the *.view Flow endpoints (each a published app surface, e.g.
// the customers dashboard). { views: [{ name, title, moduleName }], flow_project_id }.
export async function listViews() {
  return fetchData('views');
}
// Strategies available for streaming INTO a target source type (drives the
// connect-time picker). Omit targetType to list all. { target_type, strategies }
export async function getStrategies(targetType) {
  return fetchData(`strategies${targetType ? `?target_type=${encodeURIComponent(targetType)}` : ''}`);
}
export async function listNodes(workflowId) {
  return fetchData(`nodes${workflowId != null ? `?workflow_id=${workflowId}` : ''}`); // { nodes, links }
}
export async function updateNode(id, patch) {
  return fetchData(`nodes/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
}
export async function deleteNode(id) {
  return fetchData(`nodes/${id}`, { method: 'DELETE' });
}
export async function createLink(from_node_id, to_node_id) {
  return fetchData('links', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ from_node_id, to_node_id }) });
}
export async function deleteLink(id) {
  return fetchData(`links/${id}`, { method: 'DELETE' });
}
// Create a load connector node (+ data_source). File: pass { kind:'file', s3_uri }.
// DB: { kind:'postgres', source_name, host, port, database, user, password, schema }.
export async function createSourceNode(body) {
  return fetchData('data_source', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}
// Create a save connector node (+ data_target). { kind:'postgres'|'mongo', target_name, ... }.
export async function createTargetNode(body) {
  return fetchData('data_target', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

// Run an agentic source family's MASTER: turn a plain-language brief into a
// validated source config (+ live-probed sample rows). kind: 'api' | 'scraper'
// | 'enrichment' | 'rss'. Returns { ok, config, schema, sample_rows, notes, … }.
export async function buildSourceMaster(kind, brief) {
  return fetchData(`source_masters/${encodeURIComponent(kind)}/build`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ brief }) });
}

// Get a pre-signed S3 PUT url for a Load File node upload.
export async function getUploadSignedUrl(filename, content_type) {
  return fetchData('chat/upload/sign', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filename, content_type }) });
}

// Strip a UTF-8 BOM from a text upload before it goes to S3. Excel and many
// export tools prepend EF BB BF; the engine's CSV analyzer reads it into the
// first column name ("﻿Asset"), which then poisons schema, mappings, and
// rules. Binary formats (xlsx/parquet) are returned untouched.
export async function stripUtf8Bom(file) {
  try {
    const head = new Uint8Array(await file.slice(0, 3).arrayBuffer());
    if (head.length === 3 && head[0] === 0xef && head[1] === 0xbb && head[2] === 0xbf) {
      return new File([file.slice(3)], file.name, { type: file.type });
    }
  } catch { /* fall through — upload the original bytes */ }
  return file;
}

// ── Storages-strategies framework (legacy/engine-facing) ─────────────────────
// Unified Sources list: data_source + data_target as one list, each tagged with
// direction ('read'|'write') and origin. { sources: [...] }
export async function listSources() {
  return fetchData('sources');
}
// Place an EXISTING source (data_source/data_target) as a node in a workflow.
export async function placeSource({ origin, entity_id, workflow_id, position }) {
  return fetchData('nodes/place-source', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ origin, entity_id, workflow_id, position }) });
}
export async function listStorages() {
  return fetchData('storages');
}

// ── Storage connection config + unified mappings ─────────────────────────────
// Read/write a db storage's connection config (the Connection tab). origin is
// 'data_source' (read storage) or 'data_target' (write storage).
export async function getStorageConfig(origin, id) {
  return fetchData(`storage_config/${origin}/${id}`);
}
export async function updateStorageConfig(origin, id, config) {
  return fetchData(`storage_config/${origin}/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ config }) });
}
// Unified source_field_mapping rows for a storage, filtered by direction.
// direction 'read' = storage field → data_element; 'write' = data_element →
// target_table/target_column(/collection). Uses the generic CRUD filter route.
export async function listStorageMappings(storageId, direction) {
  // /api/storage_mappings/:id matches storage_id OR source_id, so mappings
  // authored for authorable source kinds (e.g. enrichment, keyed by source_id)
  // surface alongside storage_id-keyed rows. Returns { data: [...] }.
  const qs = new URLSearchParams();
  if (direction) qs.set('direction', direction);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchData(`storage_mappings/${storageId}${suffix}`);
}
export async function listStrategies() {
  return fetchData('strategy_instances'); // wired instances; /strategies is the picker
}
export async function listStrategyKinds() {
  return fetchData('strategy_kinds');
}

// Source/target inspection. Only the schema readers are live — they hit
// cortex.{source,target}.discover_schema on the paired Flow project (both
// authored on core). Connectivity + sample-row probes were removed when
// their Flow endpoints (cortex.source.test_connection, sample_rows,
// cortex.target.test_connection) turned out to never have been authored.
export async function getSourceSchema(id) {
  return fetchData(`data_source/${id}/schema`);
}
export async function getTargetSchema(id) {
  return fetchData(`data_target/${id}/schema`);
}

// Edge CRUD via generic /api/pipeline_edge
export async function createPipelineEdge(data) {
  return fetchData('pipeline_edge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}
export async function deletePipelineEdge(id) {
  return fetchData(`pipeline_edge/${id}`, { method: 'DELETE' });
}
// ── Strategy CRUD (node-graph editing) ───────────────────────────────────────
// Wire/edit/unwire one typed arrow in the pipeline DAG. The backend validates
// the kind's (domain_type → codomain_type) against the storage ids — the same
// type-check the canvas enforces before allowing a link.
export async function createStrategy({ kind, domain_storage_id, codomain_storage_id, params, workflow_id, position }) {
  return fetchData('strategies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind, domain_storage_id, codomain_storage_id, params, workflow_id, position }),
  });
}
export async function updateStrategy(id, params) {
  return fetchData(`strategies/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ params }),
  });
}
export async function deleteStrategy(id) {
  return fetchData(`strategies/${id}`, { method: 'DELETE' });
}

export async function listProcessingRules() {
  const r = await fetchData('processing_rule?sort=rule_name');
  return r.data || [];
}

// Run the project's pipeline — the typed strategy DAG over storages. Backend
// pins the current Dolt commit, topo-sorts the project's strategy rows, and
// dispatches each strategy to its kind's cortex.strategy.<kind>.apply endpoint
// (via the per-fork cortex.invoke_engine wrapper), then records a
// governance_job row. Returns { success, job_id, dolt_commit, flow_result:{
// dag, step_count, steps } } on success, or throws on Flow-side failure. There
// is no legacy single-endpoint mode.
export async function runPipeline({ params, workflow_id } = {}) {
  return fetchData('pipeline/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ params, workflow_id }),
  });
}

// Paginated pipeline-run history (job_type='pipeline_run' in governance_job).
export async function listPipelineJobs({ limit = 20, offset = 0, status } = {}) {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  if (status) params.set('status', status);
  const qs = params.toString();
  return fetchData(`pipeline/jobs${qs ? '?' + qs : ''}`);
}

// Single-job detail (full payload + result + error).
export async function getPipelineJob(id) {
  return fetchData(`pipeline/jobs/${id}`);
}

// Paginated/filterable record read for source or target. Proxies to Flow's
// cortex.{source,target}.read_records. POST so filter objects can be sent
// cleanly.
//   kind: 'source' | 'target' | 'parquet_raw' | 'parquet_processed'
//   id:   data_source.id (source/parquet) or data_target.id (target)
//   opts: { table?, filters?, limit?, offset?, order_by? }
export async function readRecords(kind, id, opts = {}) {
  // Parquet storages are pipeline artifacts keyed by their owning data source;
  // they route to the storage-records endpoint with the storage type in the path.
  if (kind === 'parquet_raw' || kind === 'parquet_processed') {
    return fetchData(`storage/${kind}/${id}/records`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    });
  }
  const base = kind === 'target' ? 'data_target' : 'data_source';
  return fetchData(`${base}/${id}/records`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
}
